import { useState, useMemo, useEffect } from 'react'
import { useCustom } from '@refinedev/core'
import type { TMessageDoc } from '@modules/types'

type TSendMessageParams = {
  url: string | null
  onSuccess?: () => Promise<void> | void
}

export const useSendMessage = ({ url, onSuccess }: TSendMessageParams) => {
  const [isSending, setIsSending] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [message, setMessage] = useState<TMessageDoc | null>(null)
  const [attachments, setAttachments] = useState<File[]>([])
  const [isUpdatingList, setIsUpdatingList] = useState(false)

  const formData = useMemo(() => {
    const data = new FormData()
    data.append('doc', message ? JSON.stringify(message) : '')
    attachments.forEach(file => {
      data.append('attachments[]', file)
    })

    return data
  }, [message, attachments])

  const { refetch } = useCustom({
    url: url ?? '',
    method: 'post',
    config: {
      payload: formData,
    },
    queryOptions: {
      enabled: false,
      onError: () => {
        setHasError(true)
        setIsSending(false)
        setIsUpdatingList(false)
      },
      onSuccess: async () => {
        setIsUpdatingList(true)

        try {
          if (onSuccess) {
            await onSuccess()
          }
        } finally {
          setIsUpdatingList(false)
          setIsSending(false)
          setMessage(null)
          setAttachments([])
        }
      },
    },
  })

  useEffect(() => {
    if (hasError) {
      const timer = setTimeout(() => {
        setHasError(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [hasError])

  const canSend = Boolean(url) && (message !== null || attachments.length > 0)

  const sendMessage = async () => {
    if (!canSend) {
      return undefined
    }

    setIsSending(true)
    setHasError(false)
    await refetch()
    return undefined
  }

  const addAttachments = (files: File[]) => {
    setAttachments(prev => [...prev, ...files])
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  return {
    message,
    attachments,
    isSending: isSending || isUpdatingList,
    hasError,
    canSend,
    sendMessage,
    addAttachments,
    removeAttachment,
    updateMessage: setMessage,
  }
}
